import { type RefObject, useEffect, useRef } from 'react'
import * as THREE from 'three'

export interface CameraControls {
  setCameraPosition1: () => void
  setCameraPosition2: () => void
  setCameraPosition3: () => void
  rotateCamera: () => void
}

const RADIUS = 3
const NUMBER_OF_HEXAGONS = 15
const HEX_SPACING = 1.05

function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function init(scene: THREE.Scene, colours: { accentColor: string; backgroundColor: string; hexagonColor: string }) {
  const { accentColor, hexagonColor } = colours

  // Hexagon shapes
  const extrudeSettings: THREE.ExtrudeGeometryOptions = {
    depth: 1,
    bevelEnabled: true,
    bevelThickness: 0.02,
    bevelSize: 0.05,
    bevelSegments: 3,
  }

  const shapes = Array.from({ length: NUMBER_OF_HEXAGONS }, () => createHexagonShape(RADIUS))
  const geometries = shapes.map((s) => new THREE.ExtrudeGeometry(s, extrudeSettings))
  const material = new THREE.MeshStandardMaterial({
    color: hexagonColor,
    metalness: 0.2,
    roughness: 0.4,
  })

  const hexagons = buildHexagons(scene, geometries, material)

  // Lighting
  addAmbientLight(scene, '#ffffff')
  addDirectionalLight(scene, '#ffffff')
  addPointLight(scene, accentColor)

  return { hexagons, shapes, geometries, material }
}

function createHexagonShape(radius: number) {
  const shape = new THREE.Shape()
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2
    const x = Math.cos(angle) * radius
    const y = Math.sin(angle) * radius
    if (i === 0) shape.moveTo(x, y)
    else shape.lineTo(x, y)
  }
  shape.closePath()
  return shape
}

function buildHexagons(scene: THREE.Scene, geometries: THREE.ExtrudeGeometry[], material: THREE.Material) {
  const hexagons = geometries.map((geometry) => {
    const hex = new THREE.Mesh(geometry, material)
    hex.castShadow = true
    hex.receiveShadow = true
    scene.add(hex)
    return hex
  })

  // Group hexagons into packs of 3
  const hexagonPacks: THREE.Mesh[][] = hexagons.reduce<THREE.Mesh[][]>((packs, hex, index) => {
    const packIndex = Math.floor(index / 3)
    if (!packs[packIndex]) packs[packIndex] = []
    packs[packIndex].push(hex)
    return packs
  }, [])

  function positionHexagonInGroup(hex: THREE.Mesh, index: number) {
    const offsetX = index === 0 ? 0 : 1
    const offsetY = index === 0 ? 0 : index === 1 ? 1 : -1
    hex.position.x = RADIUS * 1.5 * offsetX * HEX_SPACING
    hex.position.y = RADIUS * 0.875 * offsetY * HEX_SPACING
  }

  hexagonPacks.forEach((pack) => pack.forEach((hex, i) => positionHexagonInGroup(hex, i)))

  const positionRight = (hex: THREE.Mesh) => {
    hex.position.x += RADIUS * 3 * HEX_SPACING
  }
  const positionDown = (hex: THREE.Mesh) => {
    hex.position.y -= RADIUS * 1.75 * HEX_SPACING
  }
  const positionUp = (hex: THREE.Mesh) => {
    hex.position.y += RADIUS * 1.75 * HEX_SPACING
  }

  const rotateHexagonPack = (pack: THREE.Mesh[]) => {
    pack[2].position.x = RADIUS * 0 * HEX_SPACING
    pack[2].position.y = RADIUS * 0.875 * 2 * HEX_SPACING
  }

  hexagonPacks.forEach((pack, index) => {
    switch (index) {
      case 0:
        rotateHexagonPack(pack)
        break
      case 1:
        pack.forEach((h) => positionRight(h))
        pack.forEach((h) => positionDown(h))
        break
      case 2:
        pack.forEach((h) => positionRight(h))
        pack.forEach((h) => positionUp(h))
        break
      case 3:
        pack.forEach((h) => positionDown(h))
        pack.forEach((h) => positionDown(h))
        break
    }
  })

  return hexagons
}

function addAmbientLight(scene: THREE.Scene, color: string) {
  const ambientLight = new THREE.AmbientLight(color, 0.5)
  scene.add(ambientLight)
}

function addDirectionalLight(scene: THREE.Scene, color: string) {
  const directionalLight = new THREE.DirectionalLight(color, 1)
  directionalLight.position.set(5, 5, 5)
  directionalLight.castShadow = true
  directionalLight.shadow.mapSize.width = 2048
  directionalLight.shadow.mapSize.height = 2048
  directionalLight.shadow.camera.left = -20
  directionalLight.shadow.camera.right = 20
  directionalLight.shadow.camera.top = 20
  directionalLight.shadow.camera.bottom = -20
  directionalLight.shadow.camera.near = 0.5
  directionalLight.shadow.camera.far = 50
  scene.add(directionalLight)
}

function addPointLight(scene: THREE.Scene, color: string) {
  const accentLight = new THREE.PointLight(color, 1000, 50)
  accentLight.position.set(4, 4, 16)
  accentLight.castShadow = true
  accentLight.shadow.mapSize.width = 1024
  accentLight.shadow.mapSize.height = 1024
  scene.add(accentLight)
}

export function useThreeBackground(
  mountRef: RefObject<HTMLDivElement | null>,
  shouldAnimate = true,
  styles: { accentColor: string; backgroundColor: string; hexagonColor: string },
  callbacks?: {
    onCameraMoveStart?: () => void
    onCameraMoveEnd?: () => void
  }
) {
  const scene = useRef<THREE.Scene | null>(null)
  const renderer = useRef<THREE.WebGLRenderer | null>(null)
  const camera = useRef<THREE.PerspectiveCamera | null>(null)
  const cameraControls = useRef<CameraControls | null>(null)

  // Camera animation state
  const cameraAnimationActive = useRef(false)
  const cameraAnimationDuration = useRef(1000)
  const cameraAnimationStart = useRef(0)
  const cameraStartPos = new THREE.Vector3()
  const cameraEndPos = new THREE.Vector3()
  const cameraStartTarget = new THREE.Vector3()
  const cameraEndTarget = new THREE.Vector3()

  function moveCameraTo(
    camera: THREE.PerspectiveCamera,
    targetPos: THREE.Vector3,
    targetLookAt: THREE.Vector3,
    duration = 1000
  ) {
    callbacks?.onCameraMoveStart?.()
    cameraStartPos.copy(camera.position)
    cameraEndPos.copy(targetPos)
    cameraStartTarget.copy(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(10).add(camera.position))
    cameraEndTarget.copy(targetLookAt)
    cameraAnimationActive.current = true
    cameraAnimationStart.current = Date.now()
    cameraAnimationDuration.current = duration
  }

  function updateCameraAnimation() {
    if (!cameraAnimationActive.current) return
    const elapsed = Date.now() - cameraAnimationStart.current
    const progress = Math.min(elapsed / cameraAnimationDuration.current, 1)
    const eased = easeInOutCubic(progress)
    camera.current!.position.lerpVectors(cameraStartPos, cameraEndPos, eased)
    const currentTarget = new THREE.Vector3()
    currentTarget.lerpVectors(cameraStartTarget, cameraEndTarget, eased)
    camera.current!.lookAt(currentTarget)
    if (progress >= 1) {
      cameraAnimationActive.current = false
      camera.current!.lookAt(cameraEndTarget)
      callbacks?.onCameraMoveEnd?.()
    }
  }

  const handleResize = () => {
    camera.current!.aspect = window.innerWidth / window.innerHeight
    camera.current!.updateProjectionMatrix()

    if (renderer.current) {
      renderer.current.setSize(window.innerWidth, window.innerHeight)
    }
  }

  useEffect(() => {
    const mount = mountRef.current
    if (!mount) return

    const { backgroundColor } = styles

    // Scene
    const sceneInstance = new THREE.Scene()
    sceneInstance.background = new THREE.Color(backgroundColor)
    scene.current = sceneInstance

    // Initialize scene objects and lighting
    const { hexagons, shapes, geometries, material } = init(sceneInstance, styles)

    // Renderer
    renderer.current = new THREE.WebGLRenderer({ antialias: true })
    renderer.current.setSize(window.innerWidth, window.innerHeight)
    renderer.current.setPixelRatio(window.devicePixelRatio)
    renderer.current.shadowMap.enabled = true
    renderer.current.shadowMap.type = THREE.PCFShadowMap
    mount.appendChild(renderer.current.domElement)

    function setupCameras() {
      // Camera
      const cam = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 1000)
      cam.position.set(1, 1, 16)
      cam.lookAt(1, 1, 0)

      // Camera presets
      const setCameraCenteredFront = () =>
        moveCameraTo(cam, new THREE.Vector3(1, 1, 16), new THREE.Vector3(1, 1, 0), 1000)
      const setCameraLeftAngle = () =>
        moveCameraTo(cam, new THREE.Vector3(-3, -3, 5), new THREE.Vector3(2, -1, 2), 1000)
      const setCameraRightAngle = () =>
        moveCameraTo(cam, new THREE.Vector3(12, 2, 10), new THREE.Vector3(3, 1, 0), 1000)

      cameraControls.current = {
        setCameraPosition1: setCameraCenteredFront,
        setCameraPosition2: setCameraLeftAngle,
        setCameraPosition3: setCameraRightAngle,
        rotateCamera: () => {
          if (cam.position.x > 5) setCameraLeftAngle()
          else setCameraRightAngle()
        },
      }

      return cam
    }

    // Animation loop
    let animationId: number
    function animate() {
      animationId = requestAnimationFrame(animate)
      updateCameraAnimation()

      // Depth animation — each hexagon pulses with a phase offset
      for (let i = 0; i < hexagons.length; i++) {
        const phase = (i / hexagons.length) * Math.PI * 2
        const depth = 0.5 + 0.5 * Math.sin(Date.now() * 0.001 + phase)
        hexagons[i].geometry.dispose()
        hexagons[i].geometry = new THREE.ExtrudeGeometry(shapes[i], {
          depth,
          bevelEnabled: true,
          bevelThickness: 0.02,
          bevelSize: 0.05,
          bevelSegments: 3,
        })
      }

      renderer.current?.render(scene.current!, camera.current!)
    }

    camera.current = setupCameras()
    
    // Start animation loop if requested
    if (shouldAnimate) animate()
    else renderer.current.render(scene.current, camera.current)

    window.addEventListener('resize', handleResize)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('resize', handleResize)
      hexagons.forEach((h) => h.geometry.dispose())
      geometries.forEach((g) => g.dispose())
      material.dispose()
      renderer.current?.dispose()
      if (mount.contains(renderer.current!.domElement)) mount.removeChild(renderer.current!.domElement)
      cameraControls.current = null
    }
  }, [scene]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    setCameraPosition1: () => cameraControls.current?.setCameraPosition1(),
    setCameraPosition2: () => cameraControls.current?.setCameraPosition2(),
    setCameraPosition3: () => cameraControls.current?.setCameraPosition3(),
    rotateCamera: () => cameraControls.current?.rotateCamera(),
  }
}
